import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  AlertCircle,
  CheckCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  runTransaction,
  getDoc,
  increment,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const FocusFinder = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game modes: "intro", "tutorial", "play", "results"
  const [gameMode, setGameMode] = useState("intro");
  const [difficulty, setDifficulty] = useState(1); // Levels: 1, 2, 3
  const [gameCompleted, setGameCompleted] = useState(false);

  // Score and metrics
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [hitCount, setHitCount] = useState(0);
  const [commissionErrors, setCommissionErrors] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);

  // Stimuli sequence & current stimulus state
  const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
  const [stimuliSequence, setStimuliSequence] = useState([]);
  const [currentStimulus, setCurrentStimulus] = useState(null);
  const [currentResponseRecorded, setCurrentResponseRecorded] = useState(false);
  const [stimulusDisplayTime, setStimulusDisplayTime] = useState(null);

  // Timer settings (for testing we use 60 seconds; production could use 12-15 minutes)
  const GAME_DURATION = 60; // seconds
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);

  // Use a ref to always have the current value of isPlaying
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Stimulus display interval (in milliseconds)
  const STIMULUS_INTERVAL = 2000; // 2 seconds per stimulus

  // New color scheme for the category:
  const primaryColor = "from-amber-500 to-orange-600";
  const primaryColorSolid = "bg-amber-500";
  const secondaryColor = "text-amber-300";
  const tertiaryColor = "bg-amber-500/10";

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);

  // --- Logging: Fetch High Scores ---
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the single leaderboard document for the game "sustained-attention"
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "sustained-attention"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Use the current difficulty as the key (as a string)
          const scores =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scores);
        } else {
          // If the document doesn't exist, set highScores to an empty array
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // --- Timer Effect ---
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }

          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --- Generate Stimuli Sequence ---
  const generateStimuliSequence = useCallback(() => {
    const numStimuli = Math.floor((GAME_DURATION * 1000) / STIMULUS_INTERVAL);

    const sequence = [];
    // Adjust target probability based on difficulty (easier levels show more targets)
    let targetProbability;
    if (difficulty === 1) targetProbability = 0.2;
    else if (difficulty === 2) targetProbability = 0.15;
    else targetProbability = 0.1;

    for (let i = 0; i < numStimuli; i++) {
      const isTarget = Math.random() < targetProbability;
      let letter;
      if (isTarget) {
        letter = "X"; // Target letter
      } else {
        // Choose a random letter from A-Z (excluding "X" for clarity)
        const letters = "ABCDEFGHIKLMNOPQRSTUVWYZ";
        letter = letters[Math.floor(Math.random() * letters.length)];
      }
      sequence.push({ letter, isTarget });
    }

    return sequence;
  }, [difficulty]);

  // --- Start Game ---
  const startGame = () => {
    console.log("[DEBUG] Starting game.");
    setScore(0);
    setHitCount(0);
    setCommissionErrors(0);
    setMissedTargets(0);
    setReactionTimes([]);
    setCurrentStimulusIndex(0);
    setTimeLeft(GAME_DURATION);
    setIsPlaying(true);

    const sequence = generateStimuliSequence();
    setStimuliSequence(sequence);

    setTimeout(() => {
      console.log("[DEBUG] Beginning stimulus display with index 0.");
      showNextStimulus(0, sequence);
    }, 100);
  };

  // --- End Game ---
  const endGame = () => {
    console.log("[DEBUG] Ending game. Final score:", score);
    clearInterval(timerRef.current);
    setIsPlaying(false);
    setGameMode("results");
    setGameCompleted(timeLeft <= 1);
  };

  // --- Show Next Stimulus ---
  const showNextStimulus = (index, sequence) => {
    if (index >= sequence.length) {
      endGame();
      return;
    }

    // If the previous stimulus was a target and no response was made, count it as a miss
    if (
      currentStimulus &&
      currentStimulus.isTarget &&
      !currentResponseRecorded
    ) {
      setMissedTargets((prev) => prev + 1);
    }

    const newStimulus = sequence[index];

    setCurrentStimulus(newStimulus);
    setCurrentStimulusIndex(index);
    setCurrentResponseRecorded(false);
    setStimulusDisplayTime(Date.now());

    // Schedule the next stimulus using our isPlayingRef to get the latest value
    setTimeout(() => {
      if (isPlayingRef.current) {
        showNextStimulus(index + 1, sequence);
      } else {
      }
    }, STIMULUS_INTERVAL);
  };

  // --- Handle User Response ---
  const handleUserResponse = () => {
    if (!isPlayingRef.current || currentResponseRecorded) {
      return;
    }

    const responseTime = Date.now() - stimulusDisplayTime;

    setCurrentResponseRecorded(true);

    if (currentStimulus && currentStimulus.isTarget) {
      setHitCount((prev) => prev + 1);
      setScore((prev) => prev + 10);
      setReactionTimes((prev) => [...prev, responseTime]);
    } else {
      setCommissionErrors((prev) => prev + 1);
      setScore((prev) => Math.max(0, prev - 5));
    }
  };

  // --- Calculate Reaction Time Variability ---
  const calculateRTVariability = () => {
    if (reactionTimes.length < 2) return null;
    const mean =
      reactionTimes.reduce((acc, rt) => acc + rt, 0) / reactionTimes.length;
    const variance =
      reactionTimes.reduce((acc, rt) => acc + Math.pow(rt - mean, 2), 0) /
      (reactionTimes.length - 1);
    return Math.round(Math.sqrt(variance));
  };

  const compositeComparator = (a, b) => {
    // First, sort by overall score (descending)
    if (a.score !== b.score) return b.score - a.score;

    // Next, sort by reaction time variability (ascending, lower is better)
    const aVar =
      a.reactionTimeVariability !== null ? a.reactionTimeVariability : Infinity;
    const bVar =
      b.reactionTimeVariability !== null ? b.reactionTimeVariability : Infinity;
    if (aVar !== bVar) return aVar - bVar;

    // Finally, sort by average reaction time (ascending, lower is better)
    if (a.averageReactionTime !== b.averageReactionTime)
      return a.averageReactionTime - b.averageReactionTime;

    return 0;
  };

  // --- Save Score ---
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "sustained-attention",
        gameId: "sustained-attention",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        hitCount,
        commissionErrors,
        missedTargets,
        averageReactionTime:
          reactionTimes.length > 0
            ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
            : 0,
        reactionTimeVariability:
          reactionTimes.length > 1 ? calculateRTVariability() : null,
        gameLength: GAME_DURATION - timeLeft,
        timestamp: new Date(),
      };

      // 🔗 References
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        gameData.gameId
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Composite comparator (customize further if needed)
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.hitCount !== b.hitCount) return b.hitCount - a.hitCount;
        if ((a.commissionErrors || 0) !== (b.commissionErrors || 0))
          return a.commissionErrors - b.commissionErrors;
        return a.averageReactionTime - b.averageReactionTime;
      };

      // 🧾 Transaction for leaderboard + user score
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = gameData.difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);

          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 📜 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // --- Advance Tutorial ---
  const advanceTutorial = () => {
    if (tutorialStep < 2) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              navigate("/gameroom");
            }}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-amber-300">
              Attention Guardian
            </h1>
            <div className={`p-2 ${primaryColorSolid} rounded-full`}>
              <Brain className="text-amber-400" size={20} />
            </div>
          </div>
          <button
            onClick={() => {
              console.log("[DEBUG] Info button clicked.");
            }}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${primaryColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Attention Guardian
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Maintain focus over extended periods while monitoring for
                  specific events.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <AlertCircle size={24} className="text-amber-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Sustained Attention
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Builds attention stamina and vigilance
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <Clock size={24} className="text-amber-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">12-15 Minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Duration for optimal cognitive training
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className={`p-3 ${primaryColorSolid} rounded-full mb-2`}>
                    <Trophy size={24} className="text-amber-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Track your progress and compete with others
                  </p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((level) => (
                      <button
                        key={level}
                        onClick={() => {
                          console.log("[DEBUG] Difficulty changed to:", level);
                          setDifficulty(level);
                        }}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === level
                            ? `bg-gradient-to-r ${primaryColor} text-white`
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        Level {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      console.log("[DEBUG] Tutorial button clicked.");
                      setGameMode("tutorial");
                    }}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>
                  <button
                    onClick={() => {
                      console.log("[DEBUG] Play Now button clicked.");
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-amber-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            RT Variability
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          let formattedName = score.userName;
                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            formattedName = `${nameParts[0]} ${nameParts[1][0]}.`;
                          }
                          return (
                            <tr
                              key={score.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300">
                                Level {score.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.averageReactionTime
                                  ? Math.round(score.averageReactionTime) +
                                    " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.reactionTimeVariability
                                  ? Math.round(score.reactionTimeVariability) +
                                    " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-right text-amber-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4">
                Tutorial: Sustained Attention Task
              </h2>
              {tutorialStep === 0 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    In this game, you'll see a series of letters appear on the
                    screen.
                  </p>
                  <p className="text-slate-300">
                    Your goal is to press the "Target" button as quickly as
                    possible when you see the letter "X".
                  </p>
                </div>
              )}
              {tutorialStep === 1 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    When the letter "X" appears, that is your target. Press the
                    button to register your hit.
                  </p>
                  <p className="text-slate-300">
                    If you press the button when the target is not present, it
                    will count as a commission error.
                  </p>
                </div>
              )}
              {tutorialStep === 2 && (
                <div className="space-y-4">
                  <p className="text-slate-300">
                    If the target appears and you fail to press the button, it
                    will count as a missed target.
                  </p>
                  <p className="text-slate-300">
                    Try to respond as quickly and accurately as possible to
                    build your attention stamina.
                  </p>
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 2 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Gameplay Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Difficulty: Level {difficulty}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Stimulus Display */}
                <div className="flex-1 flex justify-center items-center">
                  <div
                    className={`w-32 h-32 md:w-40 md:h-40 flex items-center justify-center text-5xl font-bold ${primaryColorSolid} rounded-full`}
                  >
                    {currentStimulus ? currentStimulus.letter : ""}
                  </div>
                </div>
                {/* Game Controls */}
                <div className="flex-1 space-y-6">
                  <button
                    onClick={handleUserResponse}
                    className="w-full py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Target
                  </button>
                  <div>
                    <button
                      onClick={() => {
                        console.log("[DEBUG] End Game button clicked.");
                        if (
                          confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${primaryColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed the sustained attention challenge.
                </p>
              </div>
              {/* Explanation Box */}
              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="text-green-400 font-medium">Hits</span>:
                  Number of times you pressed at the correct target.
                  <br />
                  <span className="text-red-400 font-medium">
                    Commission Errors
                  </span>
                  : Pressing the button when it wasn't the target.
                  <br />
                  <span className="text-amber-400 font-medium">
                    Missed Targets
                  </span>
                  : Targets you didn’t respond to in time.
                  <br />
                  <span className="text-amber-400 font-medium">
                    Reaction Time Variability
                  </span>
                  : A measure of how consistently you responded. Lower = more
                  uniform reaction times.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-amber-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-amber-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Hits</span>
                      <span className="text-green-400">{hitCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Commission Errors</span>
                      <span className="text-red-400">{commissionErrors}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Targets</span>
                      <span className="text-amber-400">{missedTargets}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Reaction Time</span>
                      <span className="text-white">
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Reaction Time Variability
                      </span>
                      <span className="text-white">
                        {calculateRTVariability()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-amber-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {hitCount + commissionErrors > 0
                            ? `${Math.round(
                                (hitCount / (hitCount + commissionErrors)) * 100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-600"
                          style={{
                            width:
                              hitCount + commissionErrors > 0
                                ? `${
                                    (hitCount / (hitCount + commissionErrors)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div
                            className={`p-1 ${primaryColorSolid} rounded-full mt-0.5`}
                          >
                            <Brain size={14} className="text-amber-400" />
                          </div>
                          <span className="text-slate-300">
                            Improved attention stamina
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div
                            className={`p-1 ${primaryColorSolid} rounded-full mt-0.5`}
                          >
                            <Brain size={14} className="text-amber-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhanced vigilance
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    console.log("[DEBUG] Back to Menu clicked.");
                    setGameMode("intro");
                  }}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    console.log("[DEBUG] Play Again clicked.");
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Trophy size={18} />
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FocusFinder;
